/**
 * @file SyncProducts.js
 * @version 1.3.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-05-13
 * @modified 2026-09-07
 * @description Bulk-sync Epos Now products that aren't yet in Xero. Lists every
 *              Epos product with no manual inventory link AND no item_code
 *              auto-match; lets the admin pick rows, override item_code /
 *              account / tax / unit price defaults, and submit them to the
 *              streaming /products/bulk-create-and-link endpoint with a live
 *              progress bar. Rows with no resolved sales-account-code show
 *              red so the user can't accidentally ship them to Xero with the
 *              wrong P&L line.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './TableStyles.css';
import './SyncProducts.css';
import Input from './Input';
import LoadingSpinner from './LoadingSpinner';
import LoadingBar from './LoadingBar';
import { showAlert } from './Alert';
import ConfirmDialog from './ConfirmDialog';
import TickCheckbox from './TickCheckbox';
import SearchableDropdown from './SearchableDropdown';
import { ACCOUNT_NAMES } from '../utils/accountCodes';
import { useSlideOutButton } from './SlideOutButton';

function SyncProducts() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Per-row editable overrides keyed by eposProductId. Defaults come from the
  // server-resolved suggestions; user-edited values shadow them.
  const [overrides, setOverrides] = useState({}); // { [eposProductId]: { item_code, sales_account_code, sales_tax_type } }
  // The chart of accounts, from the same endpoints the till's product linker
  // uses — so the two places you can create an inventory item offer the same
  // codes, from the same source, rather than one offering a free-text box.
  const [salesAccounts, setSalesAccounts] = useState([]);
  const [purchaseAccounts, setPurchaseAccounts] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [salesRes, purchRes] = await Promise.all([
          fetch(`${API_URL}/api/inventory/sales-accounts`, { credentials: 'include' }),
          fetch(`${API_URL}/api/inventory/purchase-accounts`, { credentials: 'include' })
        ]);
        const salesData = await salesRes.json();
        const purchData = await purchRes.json();
        // Tolerate the older bare-string shape, as TillSales does, so a stale
        // server during a rolling deploy does not blank the dropdowns.
        const normalise = (list) => (list || [])
          .map(a => (typeof a === 'string' ? { code: a, name: null } : a))
          .filter(a => a && a.code);
        if (!alive) return;
        setSalesAccounts(normalise(salesData.accounts));
        setPurchaseAccounts(normalise(purchData.accounts));
      } catch (e) {
        console.error('Failed to fetch account codes:', e);
      }
    })();
    return () => { alive = false; };
  }, []);

  /*
   * Options for an account dropdown, with whatever the server suggested pinned
   * to the top and labelled as such.
   *
   * The suggestion was already being used — it pre-filled the text box — but
   * silently, so it read as a value somebody had entered. Saying that it is a
   * suggestion is the difference between a default that gets checked and one
   * that gets waved through.
   */
  const accountOptions = (list, suggested) => {
    const seen = new Set();
    const label = (code, name) => {
      const n = name || ACCOUNT_NAMES[code];
      return n ? `${code} — ${n}` : code;
    };
    const out = [];
    if (suggested) {
      const hit = list.find(a => String(a.code).toLowerCase() === String(suggested).toLowerCase());
      out.push({
        value: hit ? hit.code : suggested,
        label: `${label(hit ? hit.code : suggested, hit?.name)}  ·  suggested`,
        searchText: `${suggested} ${hit?.name || ACCOUNT_NAMES[suggested] || ''} suggested`
      });
      seen.add(String(hit ? hit.code : suggested).toLowerCase());
    }
    for (const a of list) {
      if (seen.has(String(a.code).toLowerCase())) continue;
      out.push({ value: a.code, label: label(a.code, a.name), searchText: `${a.code} ${a.name || ACCOUNT_NAMES[a.code] || ''}` });
    }
    return out;
  };

  const [selectedIds, setSelectedIds] = useState(new Set());

  // Filters
  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [onlySellOnTill, setOnlySellOnTill] = useState(true);
  const [hideMissingAccount, setHideMissingAccount] = useState(false);

  // Bulk sync streaming state
  const [syncing, setSyncing] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncMessage, setSyncMessage] = useState('');
  const [perRowStatus, setPerRowStatus] = useState({}); // { [eposProductId]: { status: 'pending'|'done'|'failed', error?, alreadyExisted? } }
  const [syncResult, setSyncResult] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });

  // Re-measure slide-out buttons whenever the selection toggles between
  // "nothing selected" and "something selected" (the bulk action label
  // changes width with the count).
  useSlideOutButton([selectedIds.size, syncing]);

  const loadProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/epos-now/products/unlinked`, { credentials: 'include' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setProducts(data.products || []);
      // Initialise overrides with each row's defaults so bulk-submit can just
      // read overrides[id] without falling back to the row object.
      const init = {};
      for (const p of data.products || []) {
        init[p.eposProductId] = {
          item_code: String(p.eposProductId),
          item_name: p.name,
          description: p.description || null,
          sales_account_code: p.suggestedSalesAccountCode || '',
          sales_tax_type: p.suggestedSalesTaxType || 'ZERORATEDOUTPUT',
          sales_unit_price: p.netSalePrice || null,
          // Purchase-side defaults. Every Epos product is treated as
          // purchaseable by default; if no purchase account resolves from
          // the category map the row goes through with the field blank and
          // the bulk endpoint will skip the purchase push.
          purchase_account_code: p.suggestedPurchaseAccountCode || '',
          purchase_tax_type: p.suggestedPurchaseTaxType || 'ZERORATEDINPUT',
          purchase_unit_price: p.netCostPrice || null
        };
      }
      setOverrides(init);
      setSelectedIds(new Set());
      setPerRowStatus({});
      setSyncResult(null);
    } catch (e) {
      console.error('Unlinked products fetch error:', e);
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter(p => {
      if (!showArchived && p.isArchived) return false;
      if (onlySellOnTill && !p.sellOnTill) return false;
      const ov = overrides[p.eposProductId] || {};
      const acct = ov.sales_account_code || '';
      if (hideMissingAccount && !acct) return false;
      if (q) {
        const hay = `${p.name} ${p.barcode || ''} ${p.parentCategoryName || ''} ${p.categoryName || ''} ${p.eposSupplierName || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [products, search, showArchived, onlySellOnTill, hideMissingAccount, overrides]);

  const eligibleSelectedIds = useMemo(() => (
    [...selectedIds].filter(id => {
      const ov = overrides[id];
      return ov && ov.item_code && ov.item_name && ov.sales_account_code;
    })
  ), [selectedIds, overrides]);

  const blockedSelectedCount = selectedIds.size - eligibleSelectedIds.length;

  const setOverride = (eposProductId, field, value) => {
    setOverrides(prev => ({
      ...prev,
      [eposProductId]: { ...prev[eposProductId], [field]: value }
    }));
  };

  const toggleRow = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelectedIds(prev => {
      const visibleIds = filtered.map(p => p.eposProductId);
      const allSelected = visibleIds.every(id => prev.has(id));
      const next = new Set(prev);
      if (allSelected) visibleIds.forEach(id => next.delete(id));
      else visibleIds.forEach(id => next.add(id));
      return next;
    });
  };

  const handleSync = () => {
    if (eligibleSelectedIds.length === 0) return;
    setConfirmDialog({
      isOpen: true,
      title: 'Sync to Xero',
      message: `Create ${eligibleSelectedIds.length} new inventory item${eligibleSelectedIds.length !== 1 ? 's' : ''} and push to Xero?` +
               (blockedSelectedCount > 0 ? `\n\n${blockedSelectedCount} selected row${blockedSelectedCount !== 1 ? 's' : ''} are missing an account code and will be skipped.` : ''),
      onConfirm: runSync
    });
  };

  const closeConfirm = () => setConfirmDialog({ isOpen: false, message: '', onConfirm: null });

  const runSync = async () => {
    closeConfirm();
    setSyncing(true);
    setSyncProgress(0);
    setSyncMessage('Preparing…');
    setSyncResult(null);

    // Mark all eligible selected rows as pending so the UI can show progress per-row.
    const pending = {};
    for (const id of eligibleSelectedIds) pending[id] = { status: 'pending' };
    setPerRowStatus(pending);

    const payload = {
      items: eligibleSelectedIds.map(id => {
        const ov = overrides[id] || {};
        // Treat the row as purchase-side iff a purchase account code is set
        // (either from the suggested map or typed by the user). Without it
        // the bulk endpoint wouldn't push purchase details to Xero anyway,
        // so this just keeps the local row's is_purchased flag honest.
        const hasPurchaseAccount = !!(ov.purchase_account_code && ov.purchase_account_code.trim());
        return {
          eposProductId: id,
          ...ov,
          is_sold: true,
          is_purchased: hasPurchaseAccount,
          is_tracked_as_inventory: false
        };
      })
    };

    try {
      const res = await fetch(`${API_URL}/api/epos-now/products/bulk-create-and-link`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let nl;
        while ((nl = buf.indexOf('\n')) !== -1) {
          const line = buf.slice(0, nl).trim();
          buf = buf.slice(nl + 1);
          if (!line) continue;
          let evt;
          try { evt = JSON.parse(line); } catch { continue; }
          handleEvent(evt);
        }
      }
    } catch (e) {
      console.error('Bulk sync error:', e);
      showAlert(`Bulk sync failed: ${e.message}`, 'error');
    } finally {
      setSyncing(false);
    }
  };

  const handleEvent = (evt) => {
    if (evt.type === 'progress') {
      setSyncProgress(evt.percentage || 0);
      setSyncMessage(evt.currentName
        ? `Syncing ${evt.processed + 1} of ${evt.total}: ${evt.currentName}`
        : `${evt.processed} of ${evt.total} complete`);
    } else if (evt.type === 'item_done') {
      setPerRowStatus(prev => ({
        ...prev,
        [evt.eposProductId]: {
          status: 'done',
          alreadyExisted: evt.alreadyExisted,
          xeroPushFailed: evt.xeroPushFailed
        }
      }));
    } else if (evt.type === 'item_failed') {
      setPerRowStatus(prev => ({
        ...prev,
        [evt.eposProductId]: { status: 'failed', error: evt.error }
      }));
    } else if (evt.type === 'result') {
      setSyncResult(evt);
      setSyncProgress(100);
      setSyncMessage(`${evt.created} created, ${evt.linked} linked, ${evt.failed} failed`);
      showAlert(`Sync complete: ${evt.created} created, ${evt.linked} linked, ${evt.failed} failed`,
        evt.failed > 0 ? 'warning' : 'success');
    }
  };

  const formatPrice = (n) => n == null ? '—' : `£${Number(n).toFixed(2)}`;

  const visibleAllSelected = filtered.length > 0 && filtered.every(p => selectedIds.has(p.eposProductId));

  return (
    <div className="sync-products">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">
            Sync Epos Now products that aren't yet in Xero. Tick the rows you want to sync and then click Sync to Xero.
          </span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>
              {products.length} unlinked product{products.length !== 1 ? 's' : ''} total · {filtered.length} match{filtered.length !== 1 ? '' : 'es'} the current filters · {selectedIds.size} selected
            </span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button className="btn-slideout" onClick={loadProducts} disabled={loading || syncing}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">refresh</span></div>
                <span className="btn-label">Refresh</span>
              </button>
            </div>
            <div>
              <button
                className="btn-slideout"
                onClick={handleSync}
                disabled={syncing || eligibleSelectedIds.length === 0}
              >
                <div className="icon-wrapper"><span className="material-symbols-rounded">cloud_upload</span></div>
                <span className="btn-label">
                  {syncing ? 'Syncing…' : `Sync ${eligibleSelectedIds.length || ''} to Xero`.trim()}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters">
        <div className="filter-row">
          <div className="filter-group" style={{ flex: 2 }}>
            <label>Search</label>
            <Input
              type="text"
              placeholder="Product name, barcode, category, supplier"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon="search"
            />
          </div>
          <div className="filter-group">
            <label>Show archived</label>
            <div className="checkbox-container">
              <div className="check light">
                <input id="sp-show-archived" type="checkbox"
                  checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)} />
                <label htmlFor="sp-show-archived"></label>
              </div>
            </div>
          </div>
          <div className="filter-group">
            <label>On-till only</label>
            <div className="checkbox-container">
              <div className="check light">
                <input id="sp-only-till" type="checkbox"
                  checked={onlySellOnTill}
                  onChange={(e) => setOnlySellOnTill(e.target.checked)} />
                <label htmlFor="sp-only-till"></label>
              </div>
            </div>
          </div>
          <div className="filter-group">
            <label>Hide no-account</label>
            <div className="checkbox-container">
              <div className="check light">
                <input id="sp-hide-missing" type="checkbox"
                  checked={hideMissingAccount}
                  onChange={(e) => setHideMissingAccount(e.target.checked)} />
                <label htmlFor="sp-hide-missing"></label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {syncing || syncResult ? (
        <div className="sync-progress-wrapper">
          <LoadingBar progress={syncProgress} message={syncMessage} indeterminate={false} />
        </div>
      ) : null}

      <div className="sync-products-panel">
        <div className="data-table-container">
        {loading ? (
          <div style={{ padding: 'var(--spacing-xl)', textAlign: 'center' }}><LoadingSpinner /></div>
        ) : error ? (
          <div className="sync-products-error">
            <span className="material-symbols-rounded">error</span>
            <span>{error}</span>
          </div>
        ) : (
          <table className="data-table sync-products-table">
            <thead>
              <tr>
                <th className="checkbox-col">
                  <label className="tick-check-label" style={{ justifyContent: 'center', gap: '0' }}>
                    <input
                      type="checkbox"
                      className="tick-check-input"
                      id="sync-products-select-all"
                      checked={visibleAllSelected}
                      onChange={toggleAllVisible}
                    />
                    <label htmlFor="sync-products-select-all" className="tick-check">
                      <svg width="16px" height="16px" viewBox="0 0 18 18">
                        <path d="M1,9 L1,3.5 C1,2 2,1 3.5,1 L14.5,1 C16,1 17,2 17,3.5 L17,14.5 C17,16 16,17 14.5,17 L3.5,17 C2,17 1,16 1,14.5 L1,9 Z"></path>
                        <polyline points="1 9 7 14 15 4"></polyline>
                      </svg>
                    </label>
                  </label>
                </th>
                <th style={{ width: '90px' }}>Status</th>
                <th>Product</th>
                <th>Category</th>
                <th className="text-right">Cost</th>
                <th className="text-right">Sale</th>
                <th>Tax</th>
                <th>Item code</th>
                <th>Sales acct</th>
                <th>Sales £ (net)</th>
                <th>Purchase acct</th>
                <th>Purchase £ (net)</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan="12" style={{ padding: '30px', textAlign: 'center', color: 'var(--text-muted)' }}>
                    {products.length === 0 ? 'Nothing to sync — every Epos product is already in Xero.' : 'No products match the current filters.'}
                  </td>
                </tr>
              ) : filtered.map(p => {
                const ov = overrides[p.eposProductId] || {};
                const status = perRowStatus[p.eposProductId];
                const acctMissing = !(ov.sales_account_code || '').trim();
                return (
                  <tr key={p.eposProductId} className={`${acctMissing ? 'row-missing-account' : ''} ${status?.status === 'failed' ? 'row-failed' : ''} ${status?.status === 'done' ? 'row-done' : ''}`}>
                    <td className="checkbox-col">
                      <TickCheckbox
                        id={`sync-product-${p.eposProductId}`}
                        checked={selectedIds.has(p.eposProductId)}
                        onChange={() => toggleRow(p.eposProductId)}
                        ariaLabel={p.name}
                      />
                    </td>
                    <td>
                      {!status && <span className="status-pill status-unsynced">Unsynced</span>}
                      {status?.status === 'pending' && <span className="status-pill status-pending">Syncing…</span>}
                      {status?.status === 'done' && (
                        <span className={`status-pill ${status.alreadyExisted ? 'status-linked' : status.xeroPushFailed ? 'status-warning' : 'status-done'}`}
                              title={status.alreadyExisted ? 'Linked to existing inventory item' : (status.xeroPushFailed ? 'Created locally, but Xero push failed — will retry on next sync' : 'Created + pushed to Xero')}>
                          {status.alreadyExisted ? 'Linked' : (status.xeroPushFailed ? 'Local only' : 'Synced')}
                        </span>
                      )}
                      {status?.status === 'failed' && (
                        <span className="status-pill status-failed" title={status.error}>Failed</span>
                      )}
                    </td>
                    <td>
                      <div className="product-name-cell">
                        <span className="product-name">{p.name}</span>
                        {p.isArchived && <span className="archived-badge">ARCHIVED</span>}
                        {!p.sellOnTill && <span className="off-till-badge">NOT ON TILL</span>}
                      </div>
                      <div className="product-code">EPOS-{p.eposProductId}{p.barcode ? ` · ${p.barcode}` : ''}</div>
                    </td>
                    <td>
                      <div className="category-cell">
                        <div>{p.parentCategoryName || <span style={{ color: 'var(--text-muted)' }}>—</span>}</div>
                        {p.categoryName && p.categoryName !== p.parentCategoryName && (
                          <div className="sub-category">{p.categoryName}</div>
                        )}
                      </div>
                    </td>
                    <td className="text-right">{formatPrice(p.grossCostPrice)}</td>
                    <td className="text-right">{formatPrice(p.grossSalePrice)}</td>
                    <td>{p.saleTaxGroup || '—'}</td>
                    <td>
                      <input
                        className="sp-inline-input"
                        value={ov.item_code || ''}
                        onChange={(e) => setOverride(p.eposProductId, 'item_code', e.target.value)}
                        disabled={syncing}
                      />
                    </td>
                    <td>
                      <div className={`sp-account-cell ${acctMissing ? 'missing' : ''}`}>
                        <SearchableDropdown
                          options={accountOptions(salesAccounts, p.suggestedSalesAccountCode)}
                          value={ov.sales_account_code || null}
                          onChange={(v) => setOverride(p.eposProductId, 'sales_account_code', v || '')}
                          placeholder="Pick an account"
                          searchPlaceholder="Code or name…"
                          disabled={syncing}
                        />
                      </div>
                    </td>
                    <td>
                      <input
                        className="sp-inline-input"
                        type="number"
                        step="0.01"
                        value={ov.sales_unit_price ?? ''}
                        onChange={(e) => setOverride(p.eposProductId, 'sales_unit_price', e.target.value === '' ? null : parseFloat(e.target.value))}
                        disabled={syncing}
                      />
                    </td>
                    <td>
                      <div className="sp-account-cell">
                        <SearchableDropdown
                          options={accountOptions(purchaseAccounts, p.suggestedPurchaseAccountCode)}
                          value={ov.purchase_account_code || null}
                          onChange={(v) => setOverride(p.eposProductId, 'purchase_account_code', v || '')}
                          placeholder="Pick an account"
                          searchPlaceholder="Code or name…"
                          disabled={syncing}
                        />
                      </div>
                    </td>
                    <td>
                      <input
                        className="sp-inline-input"
                        type="number"
                        step="0.01"
                        value={ov.purchase_unit_price ?? ''}
                        onChange={(e) => setOverride(p.eposProductId, 'purchase_unit_price', e.target.value === '' ? null : parseFloat(e.target.value))}
                        disabled={syncing}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmText="Sync"
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
}

export default SyncProducts;
